import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Check, Copy, ExternalLink } from "lucide-react";
import { useCopyToClipboard } from "@/hooks/useCopyToClipboard";
import { QrCode } from "@/components/ui/QrCode";
import { useUIStore } from "@/stores/useUIStore";
import {
  parseTelegramStartLink,
  telegramStartCommand,
  telegramWebChatUrl,
} from "./lib/telegramLink";

interface Props {
  /** The `https://t.me/<bot>?start=<nonce>` link minted by telegram-link-start. */
  url: string;
  /** Owned by the channel's ConnectChannelConfig — never re-declared here. */
  i18nPrefix: string;
}

/**
 * Escape hatch for browsers that cannot hand the deep link to Telegram.
 *
 * t.me's landing page finishes the handoff with `tg://resolve?...`, which a
 * browser with no Telegram app registered simply refuses ("unknown protocol"),
 * leaving the user staring at a Start button that does nothing while the nonce
 * expires. The bot's webhook treats a hand-typed `/start <nonce>` exactly like
 * the deep link, so showing the command turns that dead end into a two-tap
 * recovery instead of a support ticket.
 */
function TelegramManualFallback({ url, i18nPrefix }: Props) {
  const { t } = useTranslation();
  const addToast = useUIStore((s) => s.addToast);
  const link = parseTelegramStartLink(url);

  const { copy, copied } = useCopyToClipboard({
    onError: () =>
      addToast({ type: "error", message: t("common.copyFailed", "Couldn't copy.") }),
  });

  useEffect(() => {
    if (copied) addToast({ type: "success", message: t(`${i18nPrefix}.commandCopied`) });
  }, [copied, addToast, t, i18nPrefix]);

  // A link we can't decompose can't be turned into a chat URL or a command —
  // stay silent rather than render a half-instruction the user can't follow.
  if (!link) return null;

  const command = telegramStartCommand(link.nonce);

  return (
    <div className="space-y-2 rounded-card border border-border-default bg-surface-secondary p-3">
      <p className="text-xs text-text-muted">{t(`${i18nPrefix}.fallbackIntro`)}</p>

      {/* Scanning is listed first because it is the only path that needs
          neither a desktop Telegram app nor an existing Telegram Web session:
          the phone's camera opens the deep link in the app that is already
          logged in, payload and all. */}
      <div className="flex items-center gap-3">
        <QrCode value={url} label={t(`${i18nPrefix}.scanLabel`)} size={132} />
        <p className="text-xs text-text-muted">{t(`${i18nPrefix}.scanHint`)}</p>
      </div>

      <a
        href={telegramWebChatUrl(link.botUsername)}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 text-xs text-brand-500 hover:underline"
      >
        <ExternalLink className="h-3 w-3" />
        {t(`${i18nPrefix}.openWeb`)}
      </a>
      <p className="text-xs text-text-muted">{t(`${i18nPrefix}.fallbackSend`)}</p>
      <div className="flex items-center gap-2">
        <code className="min-w-0 flex-1 truncate rounded-input bg-surface px-2 py-1.5 font-mono text-xs text-text-primary">
          {command}
        </code>
        <button
          type="button"
          onClick={() => copy(command)}
          aria-label={t(`${i18nPrefix}.copyCommand`)}
          className="inline-flex shrink-0 items-center gap-1 rounded-input border border-border-default px-2 py-1.5 text-xs text-text-secondary hover:text-text-primary"
        >
          {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
        </button>
      </div>
    </div>
  );
}

export { TelegramManualFallback };
